import { Divider } from '@design-system';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { useTranslation } from 'next-i18next';
import { FC, useCallback, useState } from 'react';

import { DEFAULT_CURRENCY_CODE } from '../../../../constants/locations';
import { PreAuthDrawButton } from '../../../../features/draws/components/PreAuthDrawButton';
import { DrawByProductId } from '../../../../features/draws/types';
import { useDrawStage } from '../../../../hooks/useDrawStage';
import { useIsPreOrder } from '../../../../hooks/useIsPreOrder';
import { useUrlHasParam } from '../../../../hooks/useUrlHasParam';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { usePurchase } from '../../../../providers/PurchaseProvider';
import { AnalyticsFlowProvider } from '../../../../utils/analytics/flow';
import { renderShopifyArtistName } from '../../../../utils/artists';
import { isInNextSixDays } from '../../../../utils/product';
import { SimpleDialog } from '../../../Dialog/SimpleDialog';
import { DialogFlowWithStripe } from '../../Dialog/DialogFlowWithStripe';
import ProductDate from '../../ProductDate';
import { DrawExplainer } from './DrawExplainer';
import { PreOrderHeader } from './PreOrderHeader';

interface Props {
  draw: DrawByProductId;
  product: ProductType;
}

export const DrawFlow: FC<Props> = ({ draw, product }) => {
  const { t } = useTranslation('product');
  const [showDialog, setShowDialog] = useState(false);
  const stage = useDrawStage(draw);
  const isPreOrder = useIsPreOrder(draw);
  const preAuthController = usePreAuthDrawCtx();
  const purchasingController = usePurchase();
  const hasCheckoutParam = useUrlHasParam('checkout_id');

  const [emitter] = useAnalyticsEmitter();

  const handleHowToClick = useCallback(() => {
    emitter('', 'how clicked', {
      type: 'draw',
    });
  }, [, emitter]);

  return (
    <>
      {stage === 'DRAW_PRE_ORDER_CLOSED' && (
        <>
          <PreOrderHeader active={false} title={t('preOrder.isClosed.title')} />
          <Divider className="my-6" variant="dashed" />
        </>
      )}
      <div className="mb-6 flex w-full flex-col space-y-3 text-center">
        <div>
          <p className="w-full font-bold">
            {isInNextSixDays(new Date(draw.closeAt))
              ? t('subscriptionDate.draw.countdown')
              : t('subscriptionDate.draw.date')}
          </p>
          <p className="w-full">
            <ProductDate date={new Date(draw.closeAt)} />
          </p>
        </div>
        {draw.allocation && (
          <p className="w-full text-neutral-4">
            {t(
              stage === 'DRAW_PRE_ORDER_CLOSED'
                ? 'subscriptionCta.preOrder.titleAllocation'
                : 'subscriptionCta.draw.titleAllocation',
              { amount: draw.allocation },
            )}
          </p>
        )}
      </div>
      <PreAuthDrawButton
        buttonLocation="mainCta"
        draw={draw}
        hasCheckoutParam={hasCheckoutParam}
        product={product}
      />
      <button
        className="hyperlink mt-3"
        onClick={() => {
          setShowDialog(true);
          handleHowToClick();
        }}
        type="button"
      >
        {t('subscriptionCta.draw.learnMoreDialog.title')}
      </button>
      <SimpleDialog
        onClose={() => setShowDialog(false)}
        open={showDialog}
        title={t('subscriptionCta.draw.learnMoreDialog.title')}
      >
        <DrawExplainer
          cryptoComEnabled={draw.cryptoComEnabled && !isPreOrder}
        />
        {showDialog && (
          <PreAuthDrawButton
            buttonLocation="dialogCta"
            draw={draw}
            onClick={() => setShowDialog(false)}
            product={product}
          />
        )}
      </SimpleDialog>
      <AnalyticsFlowProvider
        name="draw"
        properties={{
          'product title': product.title,
          'artist name': renderShopifyArtistName(
            purchasingController.product?.artist ?? '',
          ),
          'product price':
            product.pricesAndAvailability.pricesAndCurrencies.find(
              (price) => price.currency === DEFAULT_CURRENCY_CODE,
            )?.price || 'unknown',
          'product can be framed': Boolean(
            purchasingController.frameOptions?.length,
          ),
          'product frame upgrade available': Boolean(
            purchasingController.frameOptions,
          ),
          'product frame chosen': Boolean(purchasingController.frame),
          ecommerce: {
            currency: purchasingController.product?.currency ?? '',
            transaction_id: preAuthController.entryIntentResponse.intent?.id,
            value: purchasingController.product?.price ?? '',
            items: [
              {
                item_id: product.id[0].id,
                item_name: product.title,
                item_brand: renderShopifyArtistName(
                  purchasingController.product?.artist ?? '',
                ),
                item_category: product.medium?.join(','),
                price: purchasingController.product?.price ?? '',
                quantity: purchasingController.quantity,
              },
            ],
          },
        }}
      >
        {() =>
          preAuthController.setupSecret && (
            <DialogFlowWithStripe
              clientSecret={preAuthController.setupSecret}
            />
          )
        }
      </AnalyticsFlowProvider>
    </>
  );
};
